import { Dialog, DialogActions, DialogContent, Grid, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import blue from '@mui/material/colors/blue';
import * as api from "api/Core";
import { DefaultListParameters } from "assets/appConfig";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { AppMessageBox } from "components/AppMessageBox";
import IconButtonAdd from "components/Buttons/IconButtonAdd";
import IconButtonGraph from "components/Buttons/IconButtonGraph";
import { RequestIndicator, SpinnerIndicator } from "components/CallServer";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPagination from "components/MuiExtras/MuiPagination";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import HtmlUtils from "lib/HtmlUtils";
import { KnowledgeTestDifficultyLevel, SystemRoles } from "models/ApiEnums";
import { DashboardKnowledgeAnalysisMissingTestsModel, DashboardKnowledgeTestAnalysisDetailColumn, DashboardKnowledgeTestAnalysisFilter, DashboardKnowledgeTestAnalysisMissinGraphModel, DashboardKnowledgeTestAnalysisSummaryColumn, PagedResult } from "models/ApiModels";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { WithTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import { RenderTopicModelListEvents } from "views/Dashboards/components/RenderTopicModelList";
import { RenderUserModelListEvents } from "views/Dashboards/components/RenderUserModelList";
import CreateTest from "views/KnowledgeTest/CreateTest";
import { KnowledgeTestAdminListParameters } from "views/KnowledgeTest/KnowledgeTestAdminList";

/////////////////////////////////////////////////////////////////////////////////////

interface IGraphState {
    loading: boolean;
    data: DashboardKnowledgeTestAnalysisMissinGraphModel[];
}

interface IGraphProps {
    item: DashboardKnowledgeAnalysisMissingTestsModel;
    onClose: () => void;
}

type Props = IGraphProps & WithTranslation;

class KnowledgeTestAnalysisMissingGraph extends React.PureComponent<Props, IGraphState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            loading: true
        };
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnalysisMissingGraph." + r);
    }

    ///
    private loadData() {

        this.setState({ loading: true });

        api.getDashboardKnowledgeTestAnalysis(
            {
                columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn.MissingTests,
                detailColumn: DashboardKnowledgeTestAnalysisDetailColumn.Graph,
                topicId: this.props.item.topicId,
            })
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, loading: false });
            });
    }

    ///
    private renderGraph() {
        const { data } = this.state;

        const totalTests = data ? data.map(x => x.total).reduce((p, c) => p + c) : 0;

        return <div style={{ height: 250 }} >
            <Bar
                data={
                    {
                        labels: [this.res("GraphTitle")],
                        datasets: [
                            {
                                label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange1"),
                                backgroundColor: blue[100],
                                data: [parseFloat((data[0].total / totalTests * 100.0).toFixed(1))]
                            },
                            {
                                label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange2"),
                                backgroundColor: blue[300],
                                data: [parseFloat((data[1].total / totalTests * 100.0).toFixed(1))]
                            },
                            {
                                label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange3"),
                                backgroundColor: blue[500],
                                data: [parseFloat((data[2].total / totalTests * 100.0).toFixed(1))]
                            }
                        ]
                    }
                }
                options={
                    {
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: true,
                                position: 'bottom',
                            },
                            tooltip: {
                                callbacks: {
                                    label: function (item: any) {
                                        return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${data[item.datasetIndex].total})`;
                                    },
                                }
                            }
                        },
                        scales: {
                            x: {
                                display: true,
                                stacked: true,
                                grid: {
                                    display: true
                                },
                                title: {
                                    display: true,
                                },
                                min: 0,
                                max: 100,
                                ticks: {
                                    callback: function (value: number) {
                                        return value.toFixed(0) + '%'; // convert it to percentage
                                    },
                                }
                            },
                            y: {
                                display: false,
                                stacked: true,
                                title: {
                                    display: true,
                                }
                            }
                        }
                    }
                }
            />
        </div>;
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { data, loading } = this.state;
        const totalTests = data ? data.map(x => x.total).reduce((p, c) => p + c) : 0;

        return (
            <Dialog
                open
                fullWidth
                maxWidth="md"
            >
                <MuiDialogTitleClose
                    title={this.res("HeaderTitle")}
                    subtitle={<><span>{this.props.item.name}</span><br /> {totalTests} {this.res("TestsText")}</>}
                    onClose={() => this.props.onClose()}>
                </MuiDialogTitleClose>
                <DialogContent>
                    {
                        loading
                            ? <RequestIndicator type="request" />
                            : data == null
                                ? <AppMessageBox resource="LoadGraphDataFailed" />
                                : this.renderGraph()
                    }
                </DialogContent>
                <DialogActions />
            </Dialog>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////

interface IListState {
    dialog: any;
    data: PagedResult<DashboardKnowledgeAnalysisMissingTestsModel>;
    offset: number;
    loading: boolean;
    listOption: KnowledgeTestDifficultyLevel;
    mouseOver: number;
}

interface IListProps extends RenderUserModelListEvents, RenderTopicModelListEvents {
    filter: DashboardKnowledgeTestAnalysisFilter;
}

type ListAllProps = IListProps & RouteComponentProps & WithTranslation;

class KnowledgeTestAnalysisMissingComponent extends React.PureComponent<ListAllProps, IListState> {

    constructor(props: ListAllProps) {
        super(props);

        this.state = {
            dialog: null,
            data: null,
            offset: null,
            loading: true,
            listOption: null,
            mouseOver: null,
        };
    }

    ///
    private loadData(offset: number, scrollToStartOfList: boolean = false) {

        this.setState({ loading: true });

        api.getDashboardKnowledgeTestAnalysis(
            {
                columnToSummary: DashboardKnowledgeTestAnalysisSummaryColumn.MissingTests,
                detailColumn: DashboardKnowledgeTestAnalysisDetailColumn.TopicMissingTests,
                range: { offset, take: DefaultListParameters.viewRecordsByPage },
                selectedValue: this.state.listOption,
                ...this.props.filter
            })
            .then((result) => {
                this.setState({ data: result, loading: false, offset: offset }, () => {
                    if (scrollToStartOfList) HtmlUtils.scrollToElement("divList");
                });

            })
            .catch((error) => {
                this.setState({ data: null, loading: false, offset: 0 });
            });
    }


    ///
    private handleListOption = (e: SelectChangeEvent<any>) => {
        this.setState({
            listOption: e.target.value == "All" ? null : e.target.value,
        }, () => this.loadData(0));
    };

    ///
    private getListOptions() {
        var result: Array<{ key: any, text: string; }> = [];
        result.push({ key: "All", text: this.props.t("Views.Dashboards.Common.AnyLevel") });
        result.push({ key: KnowledgeTestDifficultyLevel.Beginner, text: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange1") });
        result.push({ key: KnowledgeTestDifficultyLevel.Intermediate, text: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange2") });
        result.push({ key: KnowledgeTestDifficultyLevel.Advanced, text: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange3") });
        return result;
    }

    ///
    private renderHeader() {
        return (
            <div id="divList" >
                <MuiContainer margin="dense" >
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="h6" >
                                <b>{this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnalysisMissingList.HeaderTitle").toString().toLocaleUpperCase()}</b>
                            </Typography>
                            <MuiMuted><small>
                                {this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnalysisMissingList.HeaderSubtitle")}
                            </small></MuiMuted>
                        </Grid>

                        <Grid item>
                            <Select
                                value={this.state.listOption || "All"}
                                onChange={this.handleListOption}
                            >
                                {
                                    this.getListOptions().map((v, index) => <MenuItem key={index} value={v.key}>{v.text}</MenuItem>)
                                }
                            </Select>
                        </Grid>
                    </Grid>
                </MuiContainer>
            </div >
        );
    }

    ///
    private handleAdd(item: DashboardKnowledgeAnalysisMissingTestsModel, level: KnowledgeTestDifficultyLevel) {
        this.setState({
            dialog: (
                <CreateTest
                    initialTopic={
                        {
                            key: item.topicId,
                            label: item.name,
                            thumbnailUrl: item.thumbnailUrl,
                            canDelete: true
                        }
                    }
                    initialDifficultyLevel={level}
                    onClose={
                        () => this.setState({ dialog: null }, () => this.loadData(0))
                    }
                />
            )
        });
    }

    ///
    private handleGraph(item: DashboardKnowledgeAnalysisMissingTestsModel) {
        this.setState({
            dialog: (
                <KnowledgeTestAnalysisMissingGraph
                    {...this.props}
                    item={item}
                    onClose={
                        () => this.setState({ dialog: null })
                    }
                />
            )
        });
    }

    ///
    private drillDown(item: DashboardKnowledgeAnalysisMissingTestsModel) {
        this.props.history.push(RoutePaths.KnowledgeTestAdminList, {
            topic: {
                key: item.topicId,
                label: item.name,
                thumbnailUrl: item.thumbnailUrl,
                canDelete: true,
            }
        } as KnowledgeTestAdminListParameters);
    }

    ///
    private renderListBody() {
        const { data: { items }, mouseOver } = this.state;
        const isManager = Session.hasRole(SystemRoles.TrainingManager) || Session.hasRole(SystemRoles.TestCreator);
        const createText = this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnalysisMissingList.CreateText");

        return items.map((v, index) => {

            const missingData: Array<{ label: string, difficultyLevel: KnowledgeTestDifficultyLevel, value: number; }> = [];
            if (v.beginner > 0) missingData.push({ difficultyLevel: KnowledgeTestDifficultyLevel.Beginner, label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange1"), value: v.beginner });
            if (v.intermediate > 0) missingData.push({ difficultyLevel: KnowledgeTestDifficultyLevel.Intermediate, label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange2"), value: v.intermediate });
            if (v.advanced > 0) missingData.push({ difficultyLevel: KnowledgeTestDifficultyLevel.Advanced, label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange3"), value: v.advanced });

            const isOver = index == mouseOver;

            const tdClass = isOver ? GlobalClassKeys.overElementBackground : null;

            return (
                <TableRow
                    key={index}
                    onMouseEnter={() => this.setState({ mouseOver: index })}
                    onMouseLeave={() => this.setState({ mouseOver: null })}
                >
                    <TableCell style={{ width: 1 }} className={tdClass}>
                        {
                            isManager
                                ? (
                                    <MuiLink target="_blank" onClick={() => this.drillDown(v)}>
                                        <img src={v.thumbnailUrl} alt={v.name} width="48" height="48" />
                                    </MuiLink>
                                )
                                : <img src={v.thumbnailUrl} alt={v.name} width="48" height="48" />
                        }
                    </TableCell>

                    <TableCell className={tdClass}>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item xs>
                                {
                                    isManager
                                        ? (
                                            <MuiLink color="secondary" target="_blank" onClick={() => this.drillDown(v)}>
                                                {v.name}
                                            </MuiLink>
                                        )
                                        : <b>{v.name}</b>
                                }
                                <div>
                                    <MuiMuted>
                                        <small>
                                            <span>
                                                {
                                                    missingData.map((m, index) =>
                                                        <>
                                                            {`${m.label} (${createText} +${m.value})`}
                                                            {index < missingData.length - 1 ? <span>&nbsp;&ndash;&nbsp;</span> : null}
                                                        </>
                                                    )
                                                }
                                            </span>
                                        </small>
                                    </MuiMuted>
                                </div>
                            </Grid>

                            <Grid item>
                                <IconButtonGraph
                                    visible={isOver}
                                    size="small"
                                    onClick={() => this.handleGraph(v)}
                                />
                                <IconButtonAdd
                                    visible={isOver}
                                    size="small"
                                    onClick={() => this.handleAdd(v, missingData[0].difficultyLevel)}
                                />
                            </Grid>
                        </Grid>
                    </TableCell>
                </ TableRow>
            );
        });
    }

    ///
    private renderList() {
        const { data: { total }, dialog } = this.state;

        return (
            <MuiContainer margin="normal">
                <div>
                    {dialog}
                    {this.renderHeader()}

                    {
                        total == 0
                            ? <MuiMessageBox variant="warning" message={this.props.t("Views.Dashboards.KnowledgeTestAnalysis.AnalysisMissingList.EmptyData")} />
                            : (
                                <>
                                    <MuiContainer>
                                        <Table size="small">
                                            <TableBody >
                                                {this.renderListBody()}
                                            </TableBody>
                                        </Table>
                                    </MuiContainer>
                                    <MuiPagination
                                        limit={DefaultListParameters.viewRecordsByPage}
                                        offset={this.state.offset}
                                        total={total}
                                        onClick={(e, offset) => this.loadData(offset, true)}
                                    />
                                </>
                            )
                    }
                </div>
            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.loadData(0);
    }

    ///
    public render() {

        return (
            <div>
                {
                    this.state.loading
                        ? <SpinnerIndicator />
                        : this.state.data == null
                            ? <AppMessageBox resource="LoadListDataFailed" />
                            : this.renderList()
                }
            </div>
        );
    }
}

export default withRouter(KnowledgeTestAnalysisMissingComponent);

